
import React, { createContext, useContext, useState, useEffect } from 'react';

type DoctorPermissions = {
  canViewPayments: boolean;
};

type DoctorPermissionsContextType = {
  permissions: DoctorPermissions;
  loading: boolean;
  refreshPermissions: () => void;
};

const defaultPermissions: DoctorPermissions = {
  canViewPayments: false, // Default is off
};

const DoctorPermissionsContext = createContext<DoctorPermissionsContextType>({
  permissions: defaultPermissions,
  loading: true,
  refreshPermissions: () => {},
});

export const useDoctorPermissions = () => useContext(DoctorPermissionsContext);

export const DoctorPermissionsProvider: React.FC<{ children: React.ReactNode }> = ({ 
  children 
}) => {
  const [permissions, setPermissions] = useState<DoctorPermissions>(defaultPermissions);
  const [loading, setLoading] = useState(true);

  const fetchPermissions = async () => {
    setLoading(true);
    try {
      // In a real application, this would fetch from an API
      // For now, we'll simulate a fetch with a timeout and mock data
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Mock data - in a real app, this would come from your backend
      const mockPermissions: DoctorPermissions = {
        canViewPayments: localStorage.getItem('doctor_canViewPayments') === 'true',
      };
      
      setPermissions(mockPermissions);
    } catch (error) {
      console.error('Failed to fetch doctor permissions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPermissions();
  }, []);

  const refreshPermissions = () => {
    fetchPermissions();
  };

  return (
    <DoctorPermissionsContext.Provider 
      value={{ 
        permissions, 
        loading,
        refreshPermissions
      }}
    >
      {children}
    </DoctorPermissionsContext.Provider>
  );
};
